<!--
  The ON AIR console. This is the most important component in the product: artwork,
  what is playing, which source is carrying it, elapsed and remaining time, progress,
  start and expected end. It sits above the fold on every viewport.
-->
<script setup lang="ts">
import { computed } from 'vue';
import { formatClock } from '../clock.js';
import ShioOnAirBadge from './ShioOnAirBadge.vue';
import ShioTimecode from './ShioTimecode.vue';
import type { OnAirMode } from '../onair.js';

const props = withDefaults(
  defineProps<{
    mode: OnAirMode;
    title: string | null;
    subtitle: string | null;
    artworkUrl?: string | null;
    startedAt?: string | null;
    expectedEndAt?: string | null;
    elapsedMs: number;
    remainingMs: number;
    isRerun?: boolean;
    sourceLabel?: string | null;
    rerunLabel?: string;
    nothingOnAirLabel?: string;
    progressLabel?: string;
    elapsedLabel?: string;
    remainingLabel?: string;
  }>(),
  {
    rerunLabel: 'Rerun',
    nothingOnAirLabel: 'Nothing on air',
    progressLabel: 'Playback progress',
    elapsedLabel: 'Elapsed',
    remainingLabel: 'Remaining',
  },
);

const totalMs = computed(() => props.elapsedMs + props.remainingMs);
const progress = computed(() =>
  totalMs.value > 0 ? Math.min(100, (props.elapsedMs / totalMs.value) * 100) : 0,
);

const clock = (value: string | null | undefined): string => formatClock(value);
</script>

<template>
  <section class="shio-panel shio-nowplaying" :class="{ 'shio-panel--onair': mode === 'LIVE' }">
    <div class="shio-nowplaying__art" aria-hidden="true">
      <img v-if="artworkUrl" :src="artworkUrl" alt="" />
      <span v-else class="shio-nowplaying__art-fallback">SC</span>
    </div>

    <div class="shio-nowplaying__main">
      <div class="shio-nowplaying__status">
        <ShioOnAirBadge :mode="mode" size="large" />
        <span v-if="isRerun" class="shio-badge shio-badge--neutral">{{ rerunLabel }}</span>
        <span v-if="sourceLabel" class="shio-badge shio-badge--accent">{{ sourceLabel }}</span>
      </div>

      <h2 class="shio-display shio-nowplaying__title">
        {{ title ?? nothingOnAirLabel }}
      </h2>
      <p v-if="subtitle" class="shio-nowplaying__subtitle">{{ subtitle }}</p>

      <div
        class="shio-nowplaying__progress"
        role="progressbar"
        :aria-valuenow="Math.round(progress)"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="`${progressLabel}: ${title ?? nothingOnAirLabel}`"
      >
        <div class="shio-nowplaying__progress-fill" :style="{ width: `${progress}%` }" />
      </div>

      <div class="shio-nowplaying__times">
        <ShioTimecode :ms="elapsedMs" size="large" :label="elapsedLabel" />
        <div class="shio-nowplaying__window shio-meta">
          <span>{{ clock(startedAt) }}</span>
          <span aria-hidden="true">→</span>
          <span>{{ clock(expectedEndAt) }}</span>
        </div>
        <ShioTimecode :ms="remainingMs" size="large" :label="remainingLabel" />
      </div>
    </div>

    <div v-if="$slots.controls" class="shio-nowplaying__controls">
      <slot name="controls" />
    </div>
  </section>
</template>

<style scoped>
.shio-nowplaying {
  display: grid;
  grid-template-columns: 128px 1fr auto;
  gap: var(--shio-space-5);
  padding: var(--shio-card-padding);
  align-items: center;
}

.shio-nowplaying__art {
  width: 128px;
  height: 128px;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: hsl(var(--shio-surface-elevated));
  border: var(--shio-border-width) solid hsl(var(--shio-border));
  border-radius: var(--shio-radius-md);
}

.shio-nowplaying__art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shio-nowplaying__art-fallback {
  font-family: var(--shio-font-display);
  font-weight: 700;
  letter-spacing: 0.16em;
  color: hsl(var(--shio-accent-primary) / 0.4);
}

.shio-nowplaying__main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--shio-space-2);
}

.shio-nowplaying__status {
  display: flex;
  align-items: center;
  gap: var(--shio-space-2);
  flex-wrap: wrap;
}

.shio-nowplaying__title {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shio-nowplaying__subtitle {
  margin: 0;
  color: hsl(var(--shio-text-secondary));
}

.shio-nowplaying__progress {
  height: 6px;
  background: hsl(var(--shio-surface-elevated));
  border-radius: var(--shio-radius-pill);
  overflow: hidden;
}

.shio-nowplaying__progress-fill {
  height: 100%;
  background: linear-gradient(
    90deg,
    hsl(var(--shio-accent-primary)),
    hsl(var(--shio-accent-violet))
  );
  transition: width 1s linear;
}

.shio-nowplaying__times {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--shio-space-3);
}

.shio-nowplaying__window {
  display: flex;
  gap: var(--shio-space-2);
}

.shio-nowplaying__controls {
  display: flex;
  flex-direction: column;
  gap: var(--shio-space-2);
}

/* Responsive collapse order: controls move below, then the artwork shrinks. The
   status badge, title and timecodes never collapse. */
@media (max-width: 1023px) {
  .shio-nowplaying {
    grid-template-columns: 96px 1fr;
  }

  .shio-nowplaying__art {
    width: 96px;
    height: 96px;
  }

  .shio-nowplaying__controls {
    grid-column: 1 / -1;
    flex-direction: row;
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .shio-nowplaying {
    grid-template-columns: 64px 1fr;
    gap: var(--shio-space-3);
  }

  .shio-nowplaying__art {
    width: 64px;
    height: 64px;
  }

  .shio-nowplaying__title {
    font-size: 22px;
    line-height: 26px;
  }

  /* The two timecodes and the window between them must all fit. Shrinking the
     timecodes keeps remaining time on screen, which matters more than its size. */
  .shio-nowplaying__times :deep(.shio-timecode) {
    font-size: 22px;
    line-height: 26px;
  }

  .shio-nowplaying__window {
    font-size: 11px;
  }
}
</style>
